import { EmptyFileState } from '@/components/shared/files/EmptyFileState';
import { FileList } from '@/components/shared/files/FileList';
import { FilePreview } from '@/components/shared/files/FilePreview';
import { CenteredSpinner } from '@/components/shared/Spinner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { FileType, useFileQueries } from '@/hooks/queries/useFileQueries';
import { useAuthWithProfile } from '@/hooks/useAuth';
import { toast } from '@/hooks/useToast';
import { createFileRoute } from '@tanstack/react-router';
import { UploadIcon } from 'lucide-react';
import React, { Suspense, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

export const Route = createFileRoute('/_protected/dashboard/documents')({
  component: DocumentsDashboard,
});

function DocumentsDashboard() {
  const { profile } = useAuthWithProfile();
  const [selectedFile, setSelectedFile] = useState<FileType | null>(null);

  const {
    deleteFileMutation,
    updateFileMutation,
    createFileFromTemplateMutation,
    uploadFilesMutation,
    useFetchUserFiles,
    useFetchCommonFiles
  } = useFileQueries();

  // Use suspense queries directly in the component
  const { data: userFiles } = useFetchUserFiles(profile.user_id);
  const { data: commonFiles } = useFetchCommonFiles();

  const handleDeleteFile = (file: FileType) => {
    if (window.confirm(`Are you sure you want to delete ${file.name}?`)) {
      deleteFileMutation.mutate({ file }, {
        onSuccess: () => {
          toast({
            title: 'File deleted',
            description: `${file.name} has been deleted.`
          });

          if (selectedFile?.file_id === file.file_id) {
            setSelectedFile(null);
          }
        },
        onError: (error) => {
          console.error('Error deleting file:', error);
          toast({
            variant: 'destructive',
            title: 'Delete failed',
            description: 'The file could not be deleted.'
          });
        }
      });
    }
  };

  const handleSaveFile = ({ file, blob }: { file: FileType, blob: Blob }) => {
    if (file.url.includes('common')) {
      createFileFromTemplateMutation.mutate({
        file,
        blob,
        userId: profile.user_id,
      }, {
        onSuccess: ({ file }) => {
          toast({
            title: 'Template saved',
            description: 'The template was saved as a new file in your documents.'
          });
          setSelectedFile(file);
        }
      });
    } else if (file.url.includes('user/' + profile.user_id)) {
      updateFileMutation.mutate({
        file,
        blob,
      }, {
        onSuccess: () => {
          toast({
            title: 'File saved',
            description: 'Your changes have been saved successfully.'
          });
        }
      });
    }
  };

  // Setup file input reference for upload
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    
    const filesArray = Array.from(e.target.files);
    uploadFilesMutation.mutateAsync({
      files: filesArray,
      userId: profile.user_id
    }, {
      onSuccess({ success, error }) {
        if (success > 0) {
          toast({
            title: 'Files uploaded',
            description: `Successfully uploaded ${success} ${success === 1 ? 'file' : 'files'}${error > 0 ? `, but ${error} failed` : ''}`,
          });
        } else if (error > 0) {
          toast({
            variant: 'destructive',
            title: 'Upload failed',
            description: `Failed to upload ${error} ${error === 1 ? 'file' : 'files'}`,
          });
        }
      },
    });
    
    e.target.value = '';
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-full">
      {/* Left Column: File Navigation */}
      <Card className="flex flex-col h-full overflow-hidden col-span-1">
        <CardHeader>
          <CardTitle>Documents</CardTitle>
        </CardHeader>
        <CardContent className="flex-1 overflow-auto flex flex-col space-y-2">
          {/* File input for uploads */}
          <input
            type="file"
            ref={fileInputRef}
            className="hidden"
            onChange={handleFileUpload}
            multiple
          />
          
          {/* My Files Section */}
          <FileList
            title="My Files"
            files={userFiles}
            selectedFile={selectedFile}
            onSelectFile={setSelectedFile}
            onDeleteFile={handleDeleteFile}
            onAddFiles={() => fileInputRef.current?.click()}
            addButtonLabel="Upload Files"
            addButtonIcon={<UploadIcon className="h-4 w-4 mr-2" />}
            emptyMessage="No files uploaded yet. Click 'Upload Files' to upload your documents."
          />

          <Separator />

          {/* Templates Section */}
          <FileList
            title="Templates & Forms"
            description="Files saved from here will be added to your files"
            files={commonFiles}
            selectedFile={selectedFile}
            onSelectFile={setSelectedFile}
            emptyMessage="No templates available."
          />
        </CardContent>
      </Card>

      {/* Right Column: File Preview */}
      <Card className="h-full max-h-full col-span-1 lg:col-span-2 overflow-hidden">
        {selectedFile ? (
          <Suspense fallback={<CenteredSpinner />}>
            <ErrorBoundary fallback={<div className="p-6">Error loading file</div>}>
              <FilePreview file={selectedFile} onSave={handleSaveFile} />
            </ErrorBoundary>
          </Suspense>
        ) : (
          <EmptyFileState />
        )}
      </Card>
    </div>
  );
}